import { type EessClassification } from './solarEessClass';
import {
  DEFAULT_SOLAR_EESS_SETTINGS,
  type SolarEessSettings,
} from './solarEessSettings';

/**
 * MCS 032:2025 §6.3.2(a)-(r) / MIS 3012 §4: battery information given to the
 * customer in writing before contract. Unanswerable items print "to be
 * confirmed".
 */

/** MCS 032:2025 §6 and MIS 3012 §4, verbatim; both schemes carry it identically. */
export const EESS_IMPORTANT_NOTE =
  'Important Note: The energy performance and benefits of EESS is impossible to predict with certainty due to the numerous functions a system can be programmed to perform. This estimate is based upon the standard MCS procedure and is given as guidance only. It should not be considered as a guarantee of performance.';

export const EESS_TO_BE_CONFIRMED =
  'To be confirmed by your installer before contract';

export type EessItemKey =
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r';

/** §6.3.2, verbatim. */
export const EESS_ITEM_LABELS: Record<EessItemKey, string> = {
  a: 'The result of the performance estimate calculated in accordance with Section 5.2.1.',
  b: 'The classification of the system (see Section 2.2 in MIS 3012).',
  c: 'The battery type and any special precautions that should be taken such as ventilation and fire safety.',
  d: 'The physical characteristics of the system (size and weight).',
  e: 'The proposed location of the system.',
  f: 'Whether AC or DC coupled and if using an existing in-situ inverter.',
  g: 'Maximum power output in kilowatts (kW).',
  h: 'Maximum continuous current output in amperes (A).',
  i: 'The useable storage capacity in kilowatt-hours (kWh) accounting for the maximum allowable depth of discharge.',
  j: 'Round-trip efficiency (%) and energy loss (kWh) if charged from grid electricity.',
  k: 'Current cost of additional or replacement batteries, monoblocs or cells.',
  l: 'If capable (or not) of running in Island mode (during loss of grid power) and limitations in terms of maximum load in kW.',
  m: 'Warranties applying to the system and its storage capacity (degradation, number of cycles, energy throughput etc.)',
  n: 'How the EESS indicates its current usable capacity or state of health (thus indicating if it is ending its life or the storage capacity is below the warrantied capacity).',
  o: 'For Class 3 and Class 4 systems, the warranties provided for each discrete component.',
  p: 'End of life, recycling, arrangements in accordance with the Waste Electrical and Electronic Equipment Regulations 2013 as amended and the Batteries and Accumulators (Placing on the Market) Regulations 2008 as amended.',
  q: 'Where the EESS is to be remotely controlled by third parties, the terms of that arrangement including the terms applying should the consumer wish to terminate the arrangement and assume full control of their system. Penalties for early termination shall be clearly stated.',
  r: 'If the EESS can be controlled to respond to time of use electricity tariffs and, if so, how. It shall be highlighted whether this is a manual process (manually setting charge and discharge times) or can be automated (such that charge and discharge times change automatically when tariffs change).',
};

const EESS_ITEM_ORDER: EessItemKey[] = [
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
];

export const EESS_CHEMISTRY_LABELS: Record<string, string> = {
  LFP: 'Lithium iron phosphate (LFP)',
  NMC: 'Lithium nickel manganese cobalt (NMC)',
  LTO: 'Lithium titanate (LTO)',
  SODIUM_ION: 'Sodium-ion',
  LEAD_ACID: 'Lead acid',
  FLOW: 'Flow battery',
  OTHER: 'Other',
};

export type EessBatteryFacts = {
  manufacturer: string;
  usableCapacityKwh: number;
  maxChargeDischargeKw: number | null;
  maxContinuousCurrentA: number | null;
  chemistry: string | null;
  safetyPrecautions: string | null;
  widthMm: number | null;
  heightMm: number | null;
  depthMm: number | null;
  weightKg: number | null;
  roundTripEfficiencyPercent: number | null;
  coupling: 'AC' | 'DC' | null;
  integratedInverter: boolean | null;
  islandModeCapable: boolean | null;
  islandModeMaxLoadKw: number | null;
  warranty: {
    years: number | null;
    cycles: number | null;
    throughputMwh: number | null;
    retainedCapacityPercent: number | null;
    componentWarranties: string | null;
  } | null;
  stateOfHealthIndication: string | null;
  tariffControl: 'NONE' | 'MANUAL' | 'AUTOMATED' | null;
};

export type EessItem = {
  key: EessItemKey;
  label: string;
  /** Null prints as `EESS_TO_BE_CONFIRMED`. */
  value: string | null;
};

export type EessItemsInput = {
  battery: EessBatteryFacts;
  /** Installed count of this product. Legacy documents describe one unit. */
  quantity?: number;
  inverter: { name: string } | null;
  classification: EessClassification;
  settings: SolarEessSettings;
  /**
   * Where on the document the §5.2.1 performance estimate is, e.g. "See
   * sections D and E of the MCS performance estimate table." Null when the
   * document could not produce one.
   */
  performanceEstimateReference: string | null;
};

const format = (value: number): string =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value);

/** Round-trip loss on one full charge from the grid, kWh. */
export const eessGridChargeLossKwh = (
  usableKwh: number,
  roundTripEfficiencyPercent: number
): number =>
  Math.round((usableKwh * (100 - roundTripEfficiencyPercent)) / 10) / 10;

const lower = (text: string): string =>
  text.charAt(0).toLowerCase() + text.slice(1);

const batteryType = (battery: EessBatteryFacts): string | null => {
  const chemistry =
    battery.chemistry == null
      ? null
      : (EESS_CHEMISTRY_LABELS[battery.chemistry] ?? battery.chemistry);
  if (chemistry == null && battery.safetyPrecautions == null) return null;
  return [
    chemistry == null
      ? `Battery type: ${lower(EESS_TO_BE_CONFIRMED)}.`
      : `${chemistry}.`,
    battery.safetyPrecautions == null
      ? `Ventilation and fire safety precautions: ${lower(EESS_TO_BE_CONFIRMED)}.`
      : battery.safetyPrecautions,
  ].join(' ');
};

const physicalCharacteristics = (battery: EessBatteryFacts): string | null => {
  const { widthMm, heightMm, depthMm } = battery;
  const size =
    widthMm != null && heightMm != null && depthMm != null
      ? `${format(widthMm)} × ${format(heightMm)} × ${format(depthMm)} mm (width × height × depth)`
      : null;
  if (size == null && battery.weightKg == null) return null;
  return [
    size ?? `Size: ${lower(EESS_TO_BE_CONFIRMED)}`,
    battery.weightKg != null
      ? `${format(battery.weightKg)} kg`
      : `Weight: ${lower(EESS_TO_BE_CONFIRMED)}`,
  ].join('; ');
};

const coupling = (input: EessItemsInput): string | null => {
  const { battery, inverter } = input;
  if (
    battery.coupling == null &&
    inverter == null &&
    battery.integratedInverter !== true
  ) {
    return null;
  }
  const couplingText =
    battery.coupling == null
      ? `AC or DC coupling: ${lower(EESS_TO_BE_CONFIRMED)}.`
      : `${battery.coupling} coupled.`;
  let inverterText: string;
  if (battery.integratedInverter === true) {
    inverterText = 'The inverter is built into the battery unit.';
  } else if (inverter) {
    inverterText = `Paired with the ${inverter.name} inverter quoted on this estimate; no existing inverter is reused.`;
  } else {
    inverterText = `No new inverter is quoted on this estimate. Use of an existing in-situ inverter: ${lower(EESS_TO_BE_CONFIRMED)}.`;
  }
  return `${couplingText} ${inverterText}`;
};

const roundTrip = (battery: EessBatteryFacts): string | null => {
  if (battery.roundTripEfficiencyPercent == null) return null;
  const loss = eessGridChargeLossKwh(
    battery.usableCapacityKwh,
    battery.roundTripEfficiencyPercent
  );
  return `${format(battery.roundTripEfficiencyPercent)}% round-trip efficiency. Charging the full ${format(battery.usableCapacityKwh)} kWh from grid electricity loses about ${format(loss)} kWh each time.`;
};

const islandMode = (battery: EessBatteryFacts): string | null => {
  if (battery.islandModeCapable == null) return null;
  if (!battery.islandModeCapable) {
    return 'No. The system does not supply the home during a loss of grid power.';
  }
  return battery.islandModeMaxLoadKw == null
    ? `Yes. Maximum load during a loss of grid power: ${lower(EESS_TO_BE_CONFIRMED)}.`
    : `Yes, up to a maximum load of ${format(battery.islandModeMaxLoadKw)} kW during a loss of grid power.`;
};

const warranties = (battery: EessBatteryFacts): string | null => {
  const warranty = battery.warranty;
  if (!warranty) return null;
  const parts: string[] = [];
  if (warranty.years != null) parts.push(`${format(warranty.years)} years`);
  if (warranty.cycles != null) parts.push(`${format(warranty.cycles)} cycles`);
  if (warranty.throughputMwh != null) {
    parts.push(`${format(warranty.throughputMwh)} MWh of energy throughput`);
  }
  if (warranty.retainedCapacityPercent != null) {
    parts.push(
      `at least ${format(warranty.retainedCapacityPercent)}% of the usable capacity retained at the end of the warranty`
    );
  }
  if (parts.length === 0) return null;
  return `${parts.join('; ')}. Whichever limit is reached first applies.`;
};

const tariffControl = (battery: EessBatteryFacts): string | null => {
  switch (battery.tariffControl) {
    case 'NONE':
      return 'No. The system cannot be set to respond to time of use tariffs.';
    case 'MANUAL':
      return 'Yes, as a manual process: charge and discharge times are set by hand and must be changed by hand when the tariff changes.';
    case 'AUTOMATED':
      return 'Yes, automated: charge and discharge times change automatically when the tariff changes.';
    default:
      return null;
  }
};

const classification = (value: EessClassification): string =>
  value.classNumber == null
    ? value.description
    : `Class ${value.classNumber}. ${value.description}`;

/** The eighteen items in standard order; (o) drops for class 1/2 (not applicable). */
export const buildEessItems = (input: EessItemsInput): EessItem[] => {
  const { battery, settings } = input;
  const quantity = input.quantity ?? 1;
  const values: Record<EessItemKey, string | null> = {
    a: input.performanceEstimateReference,
    b: classification(input.classification),
    c: batteryType(battery),
    d: physicalCharacteristics(battery),
    e: null,
    f: coupling(input),
    g:
      battery.maxChargeDischargeKw == null
        ? null
        : `${format(battery.maxChargeDischargeKw)} kW`,
    h:
      battery.maxContinuousCurrentA == null
        ? null
        : `${format(battery.maxContinuousCurrentA)} A`,
    i:
      quantity > 1
        ? `${format(battery.usableCapacityKwh * quantity)} kWh (${quantity} × ${format(battery.usableCapacityKwh)} kWh usable per unit)`
        : `${format(battery.usableCapacityKwh)} kWh`,
    j: roundTrip(battery),
    k: null,
    l: islandMode(battery),
    m: warranties(battery),
    n: battery.stateOfHealthIndication,
    o: battery.warranty?.componentWarranties ?? null,
    p:
      settings.endOfLifeArrangements ??
      DEFAULT_SOLAR_EESS_SETTINGS.endOfLifeArrangements,
    q: settings.thirdPartyControlTerms,
    r: tariffControl(battery),
  };
  if (quantity > 1) {
    // Energy is additive; nameplate ratings do not establish a supported bank's
    // charging, discharge or island-mode envelope.
    for (const key of ['g', 'h', 'l'] as const) {
      const perUnit = values[key];
      values[key] =
        perUnit == null
          ? null
          : `Per unit: ${perUnit}. Installed system: ${lower(EESS_TO_BE_CONFIRMED)}.`;
    }
    for (const key of ['d', 'j', 'm'] as const) {
      if (values[key] != null) values[key] = `Per unit: ${values[key]}`;
    }
  }
  const classNumber = input.classification.classNumber;
  return EESS_ITEM_ORDER.filter(
    (key) => key !== 'o' || (classNumber !== 1 && classNumber !== 2)
  ).map((key) => ({ key, label: EESS_ITEM_LABELS[key], value: values[key] }));
};
